import { useEffect, useState } from "react";
import { supabase } from "../auth";
import {
  createObservation,
  listObservationsForPlant,
} from "../db/observations";
import type { ObservationKind } from "../db/schemas";
import type { PlantObservationRow } from "@garden/types/db";
import "./PlantObservationsTab.css";

interface Props {
  plantId: string;
}

const KINDS: { value: ObservationKind; label: string }[] = [
  { value: "general", label: "General" },
  { value: "yield", label: "Yield" },
  { value: "health", label: "Health" },
  { value: "flowering", label: "Flowering" },
  { value: "pest", label: "Pest" },
  { value: "weather", label: "Weather" },
  { value: "move", label: "Move" },
  { value: "rotation_note", label: "Rotation note" },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

export function PlantObservationsTab({ plantId }: Props) {
  const [rows, setRows] = useState<PlantObservationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const reload = async () => {
    if (!supabase) return;
    try {
      const data = await listObservationsForPlant(supabase, plantId);
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void reload();
  }, [plantId]);

  const onCreated = () => {
    setShowForm(false);
    void reload();
  };

  if (rows === null && !error) {
    return <div className="ObsTab__loading">Loading…</div>;
  }

  return (
    <div className="ObsTab">
      {!showForm && (
        <button
          type="button"
          className="ObsTab__addRow"
          onClick={() => setShowForm(true)}
        >
          + Log new observation
        </button>
      )}

      {showForm && (
        <ObservationForm
          plantId={plantId}
          onCreated={onCreated}
          onCancel={() => setShowForm(false)}
        />
      )}

      {error && <p className="ObsTab__error">{error}</p>}

      {rows && rows.length === 0 && !showForm && (
        <p className="ObsTab__empty">
          No observations yet. The journal entries you log here are the diary of
          this plant — append-only, sorted newest first.
        </p>
      )}

      <ul className="ObsTab__list">
        {rows?.map((obs) => (
          <li key={obs.id} className="ObsTab__item">
            <div className="ObsTab__itemHead">
              <span className="ObsTab__date">{formatDate(obs.observation_date)}</span>
              {obs.kind && (
                <span className="ObsTab__kind">{labelForKind(obs.kind)}</span>
              )}
            </div>
            {obs.body && <div className="ObsTab__body">{obs.body}</div>}
          </li>
        ))}
      </ul>

      {rows && rows.length > 0 && (
        <p className="ObsTab__appendOnly">
          Append-only. Logged a typo? Add a follow-up observation.
        </p>
      )}
    </div>
  );
}

function ObservationForm({
  plantId,
  onCreated,
  onCancel,
}: {
  plantId: string;
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [date, setDate] = useState(todayIso());
  const [kind, setKind] = useState<ObservationKind | "">("general");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) return;
    setSaving(true);
    setError(null);
    try {
      await createObservation(supabase, {
        plant_id: plantId,
        observation_date: date,
        kind: kind === "" ? null : kind,
        body: body.trim() === "" ? null : body,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  };

  return (
    <form className="ObsForm" onSubmit={onSubmit}>
      <div className="ObsForm__row">
        <label className="ObsForm__field">
          <span>Date</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </label>
        <label className="ObsForm__field">
          <span>Kind</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as ObservationKind | "")}
          >
            <option value="">—</option>
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="ObsForm__field">
        <span>What did you notice?</span>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={5}
          placeholder="A few lines is fine. Markdown rendering arrives in stage 08."
        />
      </label>
      {error && <p className="ObsForm__error">{error}</p>}
      <div className="ObsForm__actions">
        <button type="button" className="ObsForm__btn" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          type="submit"
          className="ObsForm__btn ObsForm__btn--primary"
          disabled={saving}
        >
          {saving ? "Saving…" : "Log observation"}
        </button>
      </div>
    </form>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function labelForKind(kind: string): string {
  const found = KINDS.find((k) => k.value === kind);
  return found ? found.label : kind;
}
